import React, { useState, useEffect, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import { InView } from 'react-intersection-observer';
import { withEndlessScroll } from '../../../HOC/endlessScroll';
import { setError } from '../../../../actions';
import { AgencyFilterResultsList } from '../AgencyFilterResultsList';
import { ItemCardPlaceholder } from '../../../Placeholders/ItemCardPlaceholder';
import { ItemCard } from '../../../ui/ItemCard';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { filteredAgenciesQuery } from '../../../../api/queries';
import {
  FilteredAgenciesQuery,
  ItemConnectionSort
} from '../../../../graphql/graphql';
import { PlaceholderWrapper, ResultBlockContainer } from './styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import { getFilterParamsToGetAgencyItems } from '../../../../utils/helpers/item';
import { SectionPlaceholder } from '../../../Placeholders/SectionPlaceholder';
import { useClient } from '../../../../hooks/useClient';
import { AgencyFilterResultsBlockProps } from './types';
import { ShowAllButton } from './ShowAllButton';
import { AgencyFilterResultBlockTitle } from './ResultBlockTitle';

const EndlessSearchItems = withEndlessScroll(AgencyFilterResultsList);
const SSR_BLOCKS_COUNT = 2;

export const AgencyFilterResultsBlock: React.FC<AgencyFilterResultsBlockProps> =
  memo(
    ({
      title,
      category,
      agencyId,
      activeCategoryId,
      isLeased,
      limit,
      cardsLimit,
      isCurrentItemPage,
      activeLeasedCategory,
      showAllHref,
      onClickItem,
      blockIndex
    }) => {
      const filterResultsBlockRef = useRef<HTMLDivElement>(null);
      const isSortingChange = useRef(false);

      const { t } = useTranslation();
      const dispatch = useDispatch();
      const { isBookmarked } = useBookmarks();
      const isClient = useClient();
      const { isSmall } = useScreenSize();

      const [skipRequest, setSkipRequest] = useState(
        isCurrentItemPage || blockIndex > SSR_BLOCKS_COUNT
      );
      const [sortingValue, setSortingValue] = useState(
        ItemConnectionSort.BumpedAtDesc
      );

      const variables = {
        filter: getFilterParamsToGetAgencyItems(
          isLeased,
          agencyId,
          category.id,
          activeCategoryId
        ),
        first: limit,
        sort: sortingValue
      };

      const { loading, data, error, fetchMore } =
        useQuery<FilteredAgenciesQuery>(filteredAgenciesQuery, {
          variables,
          skip: skipRequest,
          ...(isCurrentItemPage
            ? { ssr: false, fetchPolicy: 'network-only' as const }
            : {})
        });

      const result = data?.itemsConnection?.edges || [];
      const totalCount = data?.itemsConnection?.totalCount;
      const isSortingLoading = loading && isSortingChange.current;

      useEffect(() => {
        if (error) dispatch(setError(true));
      }, [error]);

      useEffect(() => {
        if (!loading) isSortingChange.current = false;
      }, [loading]);

      if (totalCount === 0) {
        return null;
      }

      const showAllResultHandler = () => {
        if (activeCategoryId === 'all') {
          onClickItem(category.id);
        } else {
          onClickItem(category.id, false, [activeCategoryId]);
        }
      };

      const showAllAdsText = t('pages.agency.show_all_results_many', {
        count: totalCount
      });
      const isShowSorting =
        !isCurrentItemPage && activeLeasedCategory.length === 1;
      const displayedResult =
        isCurrentItemPage || isShowSorting ? result : result.slice(0, limit);
      const isShowButton =
        !isShowSorting &&
        displayedResult.length === limit &&
        (totalCount || 0) > limit;

      return (
        <InView
          as="div"
          onChange={inView => {
            if (inView) {
              setSkipRequest(false);
            }
          }}
        >
          <ResultBlockContainer data-cy="agency-filter__results-block">
            <AgencyFilterResultBlockTitle
              title={title}
              showAllAdsText={showAllAdsText}
              showAllHref={showAllHref}
              loading={loading}
              isSortingLoading={isSortingLoading}
              isShowButton={isShowButton}
              isShowSorting={isShowSorting}
              sortingValue={sortingValue}
              showAllResultHandler={showAllResultHandler}
              onSortChange={sortValue => {
                isSortingChange.current = true;
                setSortingValue(sortValue);
              }}
            />

            {loading && !displayedResult.length && (
              <PlaceholderWrapper>
                <SectionPlaceholder noHeader>
                  {Array.from({ length: 4 }, (_, index) => (
                    <ItemCardPlaceholder key={index} isCardsLayout />
                  ))}
                </SectionPlaceholder>
              </PlaceholderWrapper>
            )}

            {!!displayedResult.length && (
              <EndlessSearchItems
                items={displayedResult || []}
                loading={loading}
                ref={filterResultsBlockRef}
                isStopLoad={
                  !data?.itemsConnection?.pageInfo?.hasNextPage ||
                  limit === cardsLimit
                }
                onLoadMore={() => {
                  fetchMore({
                    variables: {
                      filter: getFilterParamsToGetAgencyItems(
                        isLeased,
                        agencyId,
                        category.id,
                        activeCategoryId
                      ),
                      first: limit,
                      cursor: data?.itemsConnection?.pageInfo?.endCursor
                    }
                  });
                }}
              >
                {displayedResult.map(item => (
                  <ItemCard
                    key={item?.node?.id}
                    {...item?.node}
                    inBookmarks={isBookmarked(item?.node?.id)}
                    stat="agency-item-card"
                    bookmarkStat="agency-item-card-bookmark"
                    shouldUpdateCache
                  />
                ))}
              </EndlessSearchItems>
            )}

            {isShowButton && (isSmall || !isClient) && (
              <ShowAllButton
                text={showAllAdsText}
                href={showAllHref}
                onClick={showAllResultHandler}
              />
            )}
          </ResultBlockContainer>
        </InView>
      );
    }
  );

AgencyFilterResultsBlock.displayName = 'AgencyFilterResultsBlock';
